import {match} from 'ts-pattern';

import {CheckboxFilter} from '../components/CheckboxFilter/CheckboxFilter';
import {ChipsFilter} from '../components/ChipsFilter/ChipsFilter';
import {ChoiceFilter} from '../components/ChoiceFilter/ChoiceFilter';
import {DateFilter} from '../components/DateFilter/DateFilter';
import {DateRangeFilter} from '../components/DateRangeFilter/DateRangeFilter';
import {DateTimeRangeFilter} from '../components/DateTimeRangeFilter/DateTimeRangeFilter';
import {FileSizeSliderFilter} from '../components/FileSizeSliderFilter/FileSizeSliderFilter';
import {NumberSliderRangeFilter} from '../components/NumberSliderRangeFilter/NumberSliderRangeFilter';
import {RangeFilter} from '../components/RangeFilter/RangeFilter';
import {SliderFilter} from '../components/SliderFilter/SliderFilter';
import {SuggestionFilter} from '../components/SuggestionFilter/SuggestionFilter';
import {SwitchFilter} from '../components/SwitchFilter/SwitchFilter';
import {TextFilter} from '../components/TextFilter/TextFilter';
import type {EagColumn} from '../types/EagColumn';
import type {EagFilterProps} from '../types/EagFilterProps';

export const renderFilter = (column: EagColumn, filterProps: EagFilterProps) => {
  const filterDef = column.filter;

  if (!filterDef) {
    return null;
  }

  return match(filterDef.type)
    .with('TEXT', () => <TextFilter {...filterProps} quickFilters={filterDef.quickFilters} />)
    .with('SWITCH', () => <SwitchFilter {...filterProps} quickFilters={filterDef.quickFilters} />)
    .with('MULTI_CHOICE', () => (
      <ChoiceFilter
        {...filterProps}
        columnKey={column.key}
        isMulti={filterDef.multiple ?? true}
        quickFilters={filterDef.quickFilters}
      />
    ))
    .with('CHIPS', () => (
      <ChipsFilter {...filterProps} columnKey={column.key} quickFilters={filterDef.quickFilters} />
    ))
    .with('CHECKBOX', () => (
      <CheckboxFilter
        {...filterProps}
        columnKey={column.key}
        quickFilters={filterDef.quickFilters}
      />
    ))
    .with('DATE', () => (
      <DateFilter
        {...filterProps}
        min={filterDef.min != null ? String(filterDef.min) : null}
        max={filterDef.max != null ? String(filterDef.max) : null}
        relativeDates={filterDef.relativeDates ?? []}
        quickFilters={filterDef.quickFilters}
      />
    ))
    .with('DATE_RANGE', () => (
      <DateRangeFilter
        {...filterProps}
        min={filterDef.min != null ? String(filterDef.min) : null}
        max={filterDef.max != null ? String(filterDef.max) : null}
        relativeDates={filterDef.relativeDates ?? []}
        relativeInputs={filterDef.relativeInputs ?? []}
        quickFilters={filterDef.quickFilters}
      />
    ))
    .with('DATE_TIME_RANGE', () => (
      <DateTimeRangeFilter
        {...filterProps}
        min={filterDef.min != null ? String(filterDef.min) : null}
        max={filterDef.max != null ? String(filterDef.max) : null}
        relativeDates={filterDef.relativeDates ?? []}
        relativeInputs={filterDef.relativeInputs ?? []}
        quickFilters={filterDef.quickFilters}
      />
    ))
    .with('SLIDER', () => (
      <SliderFilter
        {...filterProps}
        min={typeof filterDef.min === 'number' ? filterDef.min : 0}
        max={typeof filterDef.max === 'number' ? filterDef.max : 100}
        minOpen={filterDef.minOpen ?? false}
        maxOpen={filterDef.maxOpen ?? false}
        unit={filterDef.unit ?? null}
        stepSize={filterDef.stepSize ?? null}
        steps={filterDef.steps ?? []}
        markerSize={filterDef.markerSize ?? null}
        markers={filterDef.markers}
        decimalsAllowed={filterDef.decimalsAllowed ?? false}
        inputsVisible={filterDef.inputsVisible ?? false}
        quickFilters={filterDef.quickFilters}
      />
    ))
    .with('SLIDER_RANGE', () => (
      <NumberSliderRangeFilter
        {...filterProps}
        min={typeof filterDef.min === 'number' ? filterDef.min : 0}
        max={typeof filterDef.max === 'number' ? filterDef.max : 100}
        minOpen={filterDef.minOpen ?? false}
        maxOpen={filterDef.maxOpen ?? false}
        unit={filterDef.unit ?? null}
        stepSize={filterDef.stepSize ?? null}
        steps={filterDef.steps ?? []}
        markerSize={filterDef.markerSize ?? null}
        markers={filterDef.markers ?? []}
        decimalsAllowed={filterDef.decimalsAllowed ?? false}
        inputsVisible={filterDef.inputsVisible ?? false}
        quickFilters={filterDef.quickFilters}
      />
    ))
    .with('FILE_SIZE_SLIDER', () => (
      <FileSizeSliderFilter
        {...filterProps}
        min={typeof filterDef.min === 'number' ? filterDef.min : 0}
        max={typeof filterDef.max === 'number' ? filterDef.max : 100}
        minOpen={filterDef.minOpen ?? false}
        maxOpen={filterDef.maxOpen ?? false}
        stepSize={filterDef.stepSize ?? null}
        steps={filterDef.steps ?? []}
        markerSize={filterDef.markerSize ?? null}
        markers={filterDef.markers ?? []}
        inputsVisible={filterDef.inputsVisible ?? false}
        quickFilters={filterDef.quickFilters}
      />
    ))
    .with('RANGE', () => <RangeFilter {...filterProps} quickFilters={filterDef.quickFilters} />)
    .with('MULTI_CHOICE_ASYNC', () => (
      <SuggestionFilter
        {...filterProps}
        suggestId={filterDef.suggestId ?? ''}
        quickFilters={filterDef.quickFilters}
      />
    ))
    .exhaustive();
};
